// ISDP CUSTOM: Reusable Personal Access Token manager (entitlements + create +
// one-time reveal + list/revoke). Rendered both in the internal User Settings
// "Access Tokens" tab and on the partner token page. The raw token is shown ONCE;
// only a hash is stored server-side.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useUserRole } from '../auth/useUserRole';
import { useToast } from '../feedback/ToastProvider';
import { useConfirm } from '../feedback/ConfirmProvider';
import { ConnectDialog } from './ConnectDialog';
import { mcpMessages } from '../../i18n/mcp';
import './McpAccessPage.css';
import { ALL_PRODUCTS_PREVIEW, fmtDate } from '@internal/plugin-isdp-common';

interface TokenRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  products: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revoked: boolean;
}

interface CreatedToken {
  id: string;
  name: string;
  token: string;
  prefix: string;
  products: string[];
  expiresAt: string | null;
}

export const McpTokenManager = () => {
  const config = useApi(configApiRef);
  const { fetch: authFetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const devhubUrl = config.getOptionalString('app.baseUrl') || backendUrl;
  const { products, role, loading: roleLoading } = useUserRole();
  const toast = useToast();
  const confirm = useConfirm();
  const { t } = useTranslationRef(mcpMessages);
  // Internal users (Dräger employees / admins) get FULL internal docs access via
  // MCP — the backend keys this off the token's 'internal' audience, not the
  // partner product list — so the UI shows a plain "full access" message rather
  // than the two partner product chips. Partners keep the per-product chips.
  const isInternalRole = role === 'draeger' || role === 'admin';
  const canIssue = isInternalRole || products.length > 0;

  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'token' | 'cmd' | null>(null);
  // One-click "connect your AI tool" dialog — mints + assembles snippets in one step.
  const [connectOpen, setConnectOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const resp = await authFetch(`${backendUrl}/api/mcp/tokens`);
      if (resp.ok) setTokens(await resp.json());
    } catch {
      /* best-effort */
    } finally {
      setLoading(false);
    }
  }, [authFetch, backendUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = useCallback(async () => {
    setError(null);
    if (!name.trim()) {
      setError(t('manager.errNoName'));
      return;
    }
    setCreating(true);
    try {
      const body: Record<string, unknown> = { name: name.trim() };
      if (expiresInDays.trim()) body.expiresInDays = Number(expiresInDays.trim());
      const resp = await authFetch(`${backendUrl}/api/mcp/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || t('manager.errCreate'));
        return;
      }
      setCreated(data as CreatedToken);
      toast.success(t('manager.toastCreated', { name: (data as CreatedToken).name }));
      setName('');
      setExpiresInDays('');
      await load();
    } catch (e: any) {
      setError(e.message || t('manager.errCreate'));
    } finally {
      setCreating(false);
    }
  }, [name, expiresInDays, authFetch, backendUrl, load, toast, t]);

  const handleRevoke = useCallback(
    async (id: string, tokenName: string) => {
      const ok = await confirm({
        title: t('manager.revokeTitle'),
        message: (
          <>
            <strong>{tokenName}</strong>
            {t('manager.revokeMessage')}
          </>
        ),
        confirmLabel: t('manager.revokeConfirm'),
      });
      if (!ok) return;
      try {
        const resp = await authFetch(`${backendUrl}/api/mcp/tokens/${id}`, {
          method: 'DELETE',
        });
        if (resp.ok) {
          toast.success(t('manager.toastRevoked', { name: tokenName }));
          await load();
        } else {
          toast.error(t('manager.toastRevokeFailed'));
        }
      } catch {
        toast.error(t('manager.toastRevokeFailed'));
      }
    },
    [authFetch, backendUrl, load, confirm, toast, t],
  );

  const mcpCommand = useMemo(() => {
    const token = created?.token || 'dhpat_xxxxxxxxxxxxxxxxxxxx';
    return `claude mcp add --transport http draeger-devhub \\\n  ${devhubUrl}/api/mcp/http \\\n  --header "Authorization: Bearer ${token}"`;
  }, [created, devhubUrl]);

  const copy = useCallback((text: string, which: 'token' | 'cmd') => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    });
  }, []);

  // What can a token this user creates read? Internal users get full internal
  // docs access (keyed off role, not the partner product list); partners see the
  // per-product chips; anyone with no entitlement sees the "none" copy.
  let entitlementsBody: JSX.Element;
  if (isInternalRole) {
    entitlementsBody = (
      <p className="devhub-mcp__muted">{t('manager.fullInternal')}</p>
    );
  } else if (products.length > 0) {
    entitlementsBody = (
      <p className="devhub-mcp__muted">
        {t('manager.canRead')}{' '}
        {products.map(p => (
          <span key={p} className="devhub-mcp__chip devhub-mcp__chip--product">
            {/* The simulator's every-product wildcard reads as "*" otherwise. */}
            {p === ALL_PRODUCTS_PREVIEW ? 'all products' : p}
          </span>
        ))}
      </p>
    );
  } else {
    entitlementsBody = (
      <p className="devhub-mcp__muted">{t('manager.noEntitlements')}</p>
    );
  }

  return (
    <div className="devhub-mcp__container">
      {/* Entitlements */}
      <odx-card>
        <div className="devhub-mcp__card">
          <h3>{t('manager.entitlements')}</h3>
          <p className="devhub-mcp__muted">
            {t('manager.role')} <span className="devhub-mcp__chip">{role}</span>
          </p>
          {entitlementsBody}
        </div>
      </odx-card>

      {/* Create — or, when the user has no entitlements, a clear gated card
          explaining why a token can't be issued (H27/H28). We never leave the
          section blank. */}
      {canIssue ? (
        <odx-card>
          <div className="devhub-mcp__card">
            {/* One-click connect — the fast path: mint a token and get ready-to-paste
                snippets in a single dialog. The manual form below stays for anyone
                who wants to name a token, set an expiry, or manage existing ones. */}
            <div className="devhub-mcp__quickconnect">
              <div className="devhub-mcp__quickconnect-text">
                <h3>{t('connect.quickTitle')}</h3>
                <p className="devhub-mcp__muted">{t('connect.quickBody')}</p>
              </div>
              <odx-button
                type="button"
                variant="primary"
                className="devhub-mcp__quickconnect-btn"
                // NB: `|| undefined` (not a bare boolean) so React OMITS the
                // attribute when falsy. On an <odx-*> custom element React
                // stringifies a boolean-false *update* to the literal attribute
                // disabled="false", whose mere presence the web component reads
                // as disabled — which would leave this button permanently inert
                // once roleLoading flips true→false. undefined removes it.
                disabled={roleLoading || undefined}
                onClick={() => setConnectOpen(true)}
              >
                <odx-icon slot="prefix" name="core::service" size="sm" />
                {t('connect.quickBtn')}
              </odx-button>
            </div>

            <h3>{t('manager.createTitle')}</h3>
            <div className="devhub-mcp__form">
              <label>
                <span>{t('manager.nameLabel')}</span>
                <input
                  type="text"
                  placeholder={t('manager.namePlaceholder')}
                  value={name}
                  maxLength={80}
                  onChange={e => setName(e.target.value)}
                />
              </label>
              <label>
                <span>{t('manager.expiresLabel')}</span>
                <input
                  type="number"
                  placeholder={t('manager.expiresPlaceholder')}
                  min={1}
                  max={365}
                  value={expiresInDays}
                  onChange={e => setExpiresInDays(e.target.value)}
                />
              </label>
              <odx-button
                type="button"
                variant="primary"
                disabled={creating || roleLoading || undefined}
                onClick={handleCreate}
              >
                {creating ? t('manager.creating') : t('manager.createBtn')}
              </odx-button>
            </div>
            {error && <p className="devhub-mcp__error">{error}</p>}
          </div>
        </odx-card>
      ) : (
        <odx-card>
          <div className="devhub-mcp__card devhub-mcp__gated">
            <div className="devhub-mcp__gated-icon">
              <odx-icon name="core::lock" size="lg" />
            </div>
            <div className="devhub-mcp__gated-body">
              <h3>{t('manager.gatedTitle')}</h3>
              <p className="devhub-mcp__muted">{t('manager.gatedBody')}</p>
            </div>
          </div>
        </odx-card>
      )}

      {/* One-time secret reveal */}
      {created && (
        <odx-card>
          <div className="devhub-mcp__card devhub-mcp__reveal">
            <h3>
              <odx-icon name="core::check" size="sm" /> {t('manager.created')}
            </h3>
            <p className="devhub-mcp__muted">{t('manager.createdOnce')}</p>
            <div className="devhub-mcp__secret">
              <code>{created.token}</code>
              <odx-button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => copy(created.token, 'token')}
              >
                {copied === 'token' ? t('manager.copied') : t('manager.copy')}
              </odx-button>
            </div>
            <h4>{t('manager.connectClaudeCode')}</h4>
            <div className="devhub-mcp__secret devhub-mcp__secret--cmd">
              <pre>{mcpCommand}</pre>
              <odx-button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => copy(mcpCommand, 'cmd')}
              >
                {copied === 'cmd' ? t('manager.copied') : t('manager.copy')}
              </odx-button>
            </div>
          </div>
        </odx-card>
      )}

      {/* Existing tokens */}
      <odx-card>
        <div className="devhub-mcp__card">
          <h3>{t('manager.yourTokens')}</h3>
          {loading && <p className="devhub-mcp__muted">{t('manager.loading')}</p>}
          {!loading && tokens.length === 0 && (
            <p className="devhub-mcp__muted">{t('manager.noTokens')}</p>
          )}
          {!loading && tokens.length > 0 && (
            <table className="devhub-mcp__table">
              <thead>
                <tr>
                  <th>{t('manager.colName')}</th>
                  <th>{t('manager.colPrefix')}</th>
                  <th>{t('manager.colProducts')}</th>
                  <th>{t('manager.colCreated')}</th>
                  <th>{t('manager.colLastUsed')}</th>
                  <th>{t('manager.colExpires')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.map(row => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>
                      <code>{row.prefix}…</code>
                    </td>
                    <td>{isInternalRole ? t('manager.fullInternalShort') : row.products.join(', ') || '—'}</td>
                    <td>{fmtDate(row.createdAt)}</td>
                    <td>{fmtDate(row.lastUsedAt)}</td>
                    <td>{fmtDate(row.expiresAt)}</td>
                    <td>
                      <odx-button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevoke(row.id, row.name)}
                      >
                        {t('manager.revoke')}
                      </odx-button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </odx-card>

      <ConnectDialog
        open={connectOpen}
        onClose={() => {
          setConnectOpen(false);
          // A quick-connect mint creates a real token; refresh the list so it appears.
          load();
        }}
        defaultName="Claude Code"
      />
    </div>
  );
};
